import * as React from "react";
import { useTranslation } from "react-i18next";

import styles from "./InvitePlayers.module.scss";
import classNames from "classnames";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/pro-solid-svg-icons";
import { AbsoluteCenter } from "../../AbsoluteCenter";
import { Loader } from "../../Loader";
import { ProfileImage } from "../../ProfileImage";
import { ButtonWithSound, LinkWithSound } from "../../AudioProvider";
import personBg from "./assets/player-item-photo-bg.png";
import { useMutation } from "../../../hooks/useMutation";
import {
  InstantTournamentInvitePlayerMutation,
  InstantTournamentInvitePlayerMutationVariables,
} from "./graphql/InstantTournamentInvitePlayerMutation.generated";
import { loader } from "graphql.macro";

const INVITE_MUTATION = loader(
  "./graphql/InstantTournamentInvitePlayerMutation.graphql"
);

type Player = {
  id: string;
  name: string;
};

type InvitePlayersProps = React.PropsWithChildren<{
  tournamentId: string;
  players: Array<Player> | null;
  exclude: Array<string>;
  onClose?: () => void;
}>;

const InvitePlayerContext = React.createContext<Player>(undefined as any);

const InvitePlayersContent = ({
  exclude,
  players,
  children,
  tournamentId,
}: InvitePlayersProps) => {
  const { t } = useTranslation();

  const [, invitePlayer] = useMutation<
    InstantTournamentInvitePlayerMutation,
    InstantTournamentInvitePlayerMutationVariables
  >(INVITE_MUTATION);

  const filteredPlayers: Array<Player> | null = React.useMemo(() => {
    if (players === null) return null;
    if (exclude.length === 0) return players;

    return players.filter(({ id }) => exclude.indexOf(id) < 0);
  }, [exclude, players]);

  if (filteredPlayers === null)
    return (
      <AbsoluteCenter>
        <Loader description={t("loading_players")} />
      </AbsoluteCenter>
    );
  else if (filteredPlayers.length === 0)
    return (
      <AbsoluteCenter>
        <div className="p-4 text-center">
          <span className="text-white font-bold">
            {t("no_invitations_available")}
          </span>
        </div>
      </AbsoluteCenter>
    );
  return (
    <div className="flex flex-col h-full">
      <div className={classNames("flex-1", "overflow-auto", styles.scrollable)}>
        {filteredPlayers.map((player, index) => (
          <div
            className={classNames(
              "px-4",
              "py-2",
              "text-white",
              "flex",
              "items-center",
              "justify-between"
            )}
            style={{
              backgroundColor: "#295994",
              borderBottom: "2px solid #294163",
            }}
            key={player.id}
          >
            <div className="flex -mx-1 items-center">
              <div className="px-1 flex-shrink-0">
                <div
                  className={classNames(
                    "flex items-center justify-center",
                    styles.pairPhoto
                  )}
                  style={{
                    background: `url(${personBg}) center center no-repeat`,
                  }}
                >
                  <ProfileImage
                    id={player.id}
                    name={player.name}
                    className="rounded bg-white"
                  />
                </div>
              </div>
              <div className="px-1 flex-1 overflow-hidden">
                <div className="flex flex-col justify-between">
                  <span className="truncate">{player.name}</span>
                  <InvitePlayerContext.Provider value={player}>
                    {children}
                  </InvitePlayerContext.Provider>
                </div>
              </div>
            </div>
            <div>
              <button
                className="btn btn-orange-600 rounded-md text-white text-base font-semibold w-32"
                style={{ boxShadow: "0px 3px 0px 0px #874A23" }}
                onClick={() =>
                  invitePlayer({
                    input: {
                      instantTournamentId: tournamentId,
                      playerId: player.id,
                    },
                  })
                }
              >
                {t("invite")}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const InvitePlayerButton = ({
  onClick,
  className,
  disabled,
  ...rest
}: {
  onClick: (player: Player) => Promise<any>;
} & Omit<JSX.IntrinsicElements["button"], "onClick">) => {
  const player = useInvitePlayerContext();
  return (
    <ButtonWithSound
      className={classNames(
        "btn",
        "btn-sm",
        "bg-orange-700",
        "text-white",
        "border",
        "border-orange-900",
        "rounded",
        styles.button,
        { "hover:bg-orange-900": !disabled },
        className
      )}
      onClick={() => onClick(player)}
      disabled={disabled}
      {...rest}
    />
  );
};

export const useInvitePlayerContext = (): Player => {
  return React.useContext(InvitePlayerContext);
};

export const InvitePlayersModal = ({
  tournamentId,
  onClose,
  ...rest
}: InvitePlayersProps) => {
  const { t } = useTranslation();

  return (
    <>
      <div className="absolute bg-black-alpha-30 top-0 left-0 right-0 bottom-0 z-20" />
      <div
        className="w-1/3 absolute shadow-2xl bg-blue-800 top-0 left-0 bottom-0 z-30"
        aria-label={t("invite_players")}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between text-white flex-shrink-0 mb-4 -mx-2 p-4 pb-0">
            <span className="font-bold text-lg truncate px-2">
              {t("instant_tournament.invite_friend")}
            </span>
            <div className="px-2 flex-shrink-0">
              <button onClick={onClose}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-hidden relative">
            <InvitePlayersContent {...rest} tournamentId={tournamentId} />
          </div>
        </div>
      </div>
    </>
  );
};
